import type { ReferenceReviewStatus, ReferenceSnapshot } from "@/types";

/**
 * The URL state of `/test-cases/references`.
 *
 * `view` is the tab. `tc` (with `snap`, one of its versions) is the TC the
 * 검토 tab has open — how the 생성 tab's "검토 탭에서 열기" lands on it.
 * Normalization, not validation: an unknown view is 생성.
 */
export type ReferenceView = "generate" | "review" | "passed";

export type ReferenceSearch = {
  view: ReferenceView;
  tc: string | null;
  snap: string | null;
};

export const REFERENCE_ROUTE = "/test-cases/references" as const;
export const REFERENCE_DEFAULT_SEARCH: ReferenceSearch = { view: "generate", tc: null, snap: null };
/** Where the sidebar sends a person: the queue of versions waiting for review. */
export const REFERENCE_REVIEW_SEARCH: ReferenceSearch = { view: "review", tc: null, snap: null };

function asText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function parseReferenceSearch(raw: Record<string, unknown>): ReferenceSearch {
  const view = raw.view === "review" || raw.view === "passed" ? raw.view : "generate";
  return { view, tc: asText(raw.tc), snap: asText(raw.snap) };
}

export const REVIEW_LABEL: Record<ReferenceReviewStatus, string> = {
  not_passed: "미통과",
  passed: "통과",
  rejected: "반려",
};

/**
 * `review` is the version's pass: a note alone leaves it 미통과. Without a
 * pass, a version whose last pass-or-rejection is a rejection is 반려 (the
 * list says so in `decision`, the version list in `reviews`, newest first).
 */
export const reviewStatus = (
  s: Pick<ReferenceSnapshot, "review"> & Partial<Pick<ReferenceSnapshot, "reviews">> & { decision?: string | null },
): ReferenceReviewStatus => {
  if (s.review?.decision === "passed") return "passed";
  if (s.decision === "rejected") return "rejected";
  const last = s.reviews?.find((r) => r.decision === "passed" || r.decision === "rejected");
  return last?.decision === "rejected" ? "rejected" : "not_passed";
};

export const REVIEW_TONE: Record<ReferenceReviewStatus, string> = {
  not_passed: "border-accent-line bg-accent text-accent-foreground",
  passed: "border-success-line bg-success-surface text-success",
  rejected: "border-destructive-line bg-destructive-surface text-destructive",
};

/** Shown before a rejection is sent: the answer has not said yet which version scoring will read. */
export const REJECT_CONFIRM_TEXT =
  "이 버전의 승인이 해제됩니다. 채점 Reference가 다른 버전으로 바뀌거나 없어질 수 있습니다. 이 TC는 사람이 다시 통과시킬 때까지 루프 대상에서 빠집니다.";

/** After a rejection: the version the answer says scoring reads now. */
export const rejectedResultText = (activeVersion: number | null) =>
  activeVersion == null ? "채점 Reference가 없습니다." : `채점 Reference가 v${activeVersion}(으)로 바뀌었습니다.`;

export type DiffLine = { kind: "same" | "add" | "del"; text: string };

/** Lines past this on either side are not compared (the table would be too big); null then. */
export const DIFF_LINE_LIMIT = 600;

/** A line diff of `before` → `after` (longest common subsequence), in reading order. */
export function lineDiff(before: string, after: string): DiffLine[] | null {
  const a = before.split("\n");
  const b = after.split("\n");
  if (a.length > DIFF_LINE_LIMIT || b.length > DIFF_LINE_LIMIT) return null;
  // lcs[i][j]: length of the common subsequence of a[i..] and b[j..].
  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: "del", text: a[i] });
      i += 1;
    } else {
      out.push({ kind: "add", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) out.push({ kind: "del", text: a[i++] });
  while (j < b.length) out.push({ kind: "add", text: b[j++] });
  return out;
}
